import {ComponentStore} from '@ngrx/component-store';
import {Injectable} from '@angular/core';
import {PublicReportData, PublicReportsReportState} from './public-reports';
import {map, Observable, Subscription, switchMap, tap, withLatestFrom, combineLatest} from 'rxjs';
import {Router} from '@angular/router';
import {PublicReportsService} from '../../services/public-reports.service';
import {Serializer, SerializerResponse, Serializers} from '../../interfaces/serializer';
import {SavedReport} from '../../interfaces/saved-reports';
import {AdRequestParams, AdRPeriod, AdRReportType} from '../../interfaces/ad-reports';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {initLanguages} from '../languages/languages.actions';
import {loadPublicChannels} from '../channels/channels.actions';
import {initPublic} from '../init/init.actions';
import {Actions, ofType} from '@ngrx/effects';
import {reportOpenPeriodDialog, reportUpdateSettings} from '../report/report.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {DimensionComponentObject} from '../../classes/dimension-component-object';
import {MetricComponentObject} from '../../classes/metric-component-object';
import {dialogOpen} from '../dialog/dialog.actions';
import {
  ReportPeriodSettingDialogComponent
} from '../../components/report/report-period-setting-dialog/report-period-setting-dialog.component';
import {filter} from 'rxjs/operators';
import {Channel} from '../../interfaces/channel';
import {selectChannels, selectChannelsLoaded} from '../channels/channels.selectors';
import {selectDimensionComponentObjects, selectDimensionsLoaded, selectMetricComponentObjects, selectMetricsLoaded} from '../selectors';
import {loadPublicGeneralDimensions} from '../generalDimensions/generalDimensions.actions';
import {loadPublicDataSetConditionalDimensions} from '../dataSetConditionalDimensions/dataSetConditionalDimensions.actions';
import {loadPublicDataSetDimensions} from '../dataSetDimensions/dataSetDimensions.actions';
import {loadPublicDataSetMetrics} from '../dataSetMetrics/dataSetMetrics.actions';

@Injectable()
export class PublicReportsReportStore extends ComponentStore<PublicReportsReportState> {
  public readonly publicReportData$: Observable<PublicReportData> = this.select((state: PublicReportsReportState): PublicReportData => state.publicReportData);
  public readonly report$: Observable<Serializer<SavedReport>> = this.select((state: PublicReportsReportState): Serializer<SavedReport> => state.report);
  public readonly reportType$: Observable<AdRReportType> = this.select((state: PublicReportsReportState): AdRReportType => state.reportType);
  public readonly reportName$: Observable<string> = this.select((state: PublicReportsReportState): string => state.reportName);
  public readonly createdBy$: Observable<string> = this.select((state: PublicReportsReportState): string => state.createBy);

  public readonly reportDimensions$: Observable<Array<DimensionComponentObject> | null> = this.select((state: PublicReportsReportState): Array<DimensionComponentObject> => state.reportDimensions);
  public readonly reportMetrics$: Observable<Array<MetricComponentObject> | null> = this.select((state: PublicReportsReportState): Array<MetricComponentObject> => state.reportMetrics);
  public readonly reportConversionMetric$: Observable<MetricComponentObject | null | undefined> = this.select((state: PublicReportsReportState): MetricComponentObject => state.reportConversionMetric);
  public readonly reportChannel$: Observable<Serializer<Channel> | null | undefined> = this.select((state: PublicReportsReportState): Serializer<Channel> => state.reportChannel);
  public readonly reportPeriod$: Observable<AdRPeriod | null> = this.select((state: PublicReportsReportState): AdRPeriod => state.report.attributes.parameters.period);

  public readonly settingsLoaded$: Observable<boolean> = combineLatest([
    this._store.select(selectDimensionsLoaded),
    this._store.select(selectMetricsLoaded),
    this._store.select(selectChannelsLoaded)
  ]).pipe(
    map(([dimensions, metrics, channels]: [boolean, boolean, boolean]): boolean => dimensions && metrics && channels)
  );

  public readonly drawerIsOpened$: Observable<boolean> = this.select((state: PublicReportsReportState): boolean => state.drawerIsOpened)

  public readonly loading$: Observable<boolean> = this.select((state: PublicReportsReportState): boolean => state.loading);
  public readonly loaded$: Observable<boolean> = this.select((state: PublicReportsReportState): boolean => state.loaded);

  private readonly _loadReport: () => void = this.effect((params$: Observable<void>): Observable<any> => params$.pipe(
    tap((): void => this.patchState({ loading: true })),
    map((): { uuid: string, token: string } => this._router.getCurrentNavigation().extras.state as { uuid: string, token: string }),
    tap((routerState: { uuid: string, token: string }): void => {
      this._store.dispatch(initLanguages({ lang: 'en' }));
      this._store.dispatch(initPublic({ uuid: routerState.uuid, token: routerState.token }));
    }),
    switchMap((routerState: { uuid: string, token: string }): Observable<any> => this._service.getReport(routerState.uuid, routerState.token).pipe(
      tap((response: SerializerResponse<Serializer<SavedReport>>): void => {
        this.patchState({
          report: response.data,
          reportType: response.data.attributes.report_type,
          reportName: response.data.attributes.name,
          createBy: response.data.attributes.created_by_email,
          uuid: routerState.uuid,
          token: routerState.token,
          publicReportData: {
            report: response.data,
            uuid: routerState.uuid,
            token: routerState.token
          },
          loading: false,
          loaded: true
        });

        this._store.dispatch(loadPublicGeneralDimensions({ uuid: routerState.uuid, token: routerState.token }));
        this._store.dispatch(loadPublicDataSetDimensions({ uuid: routerState.uuid, token: routerState.token }));
        this._store.dispatch(loadPublicDataSetConditionalDimensions({ uuid: routerState.uuid, token: routerState.token }));
        this._store.dispatch(loadPublicDataSetMetrics({ uuid: routerState.uuid, token: routerState.token }));
        this._store.dispatch(loadPublicChannels({ uuid: routerState.uuid, token: routerState.token }));
      })
    )),
  ));

  private readonly _updateSettings$: Subscription = this.effect((): Observable<[TypedAction<string> & { settings: Partial<AdRequestParams> }, PublicReportsReportState]> => this._actions$.pipe(
    ofType(reportUpdateSettings),
    withLatestFrom(this.state$),
    tap(([action, state]: [TypedAction<string> & { settings: Partial<AdRequestParams> }, PublicReportsReportState]): void => {
      const parameters: Partial<AdRequestParams> = {
        ...state.report.attributes.parameters,
        ...action.settings
      };

      this.patchState({
        report: {
          ...state.report,
          attributes: {
            ...state.report.attributes,
            parameters: parameters
          }
        },
      });
    })
  ));

  private readonly _periodSettingDialog$: Subscription = this.effect((): Observable<[TypedAction<string>, PublicReportsReportState]> => this._actions$.pipe(
    ofType(reportOpenPeriodDialog),
    withLatestFrom(this.state$),
    tap(([action, state]: [TypedAction<string> & { disableComparison?: boolean }, PublicReportsReportState]): void => {
      this._store.dispatch(dialogOpen({
        component: ReportPeriodSettingDialogComponent,
        config: {
          width: '650px',
          height: 'auto'
        },
        data: {
          report: state.report,
          disableComparison: action.disableComparison || false
        }
      }));
    })
  ));

  private readonly _dimensions$: Subscription = this.effect((): Observable<[Array<DimensionComponentObject>, PublicReportsReportState]> => this._store.select(selectDimensionComponentObjects)
    .pipe(
      withLatestFrom(this.state$),
      filter(([_, state]: [Array<DimensionComponentObject>, PublicReportsReportState]): boolean => !!state.report && !!state.report.attributes.parameters.dimensions),
      tap(([dimensions, state]: [Array<DimensionComponentObject>, PublicReportsReportState]): void => {
        this.patchState({
          reportDimensions: state.report.attributes.parameters.dimensions
            .map((slug: string): DimensionComponentObject | undefined => dimensions.find((dimension: DimensionComponentObject): boolean => dimension.payload.attributes.slug === slug))
            .filter((dimension: DimensionComponentObject | undefined): boolean => !!dimension)
        });
      })
    )
  );

  private readonly _metrics$: Subscription = this.effect((): Observable<[Array<MetricComponentObject>, PublicReportsReportState]> => this._store.select(selectMetricComponentObjects)
    .pipe(
      withLatestFrom(this.state$),
      filter(([_, state]: [Array<MetricComponentObject>, PublicReportsReportState]): boolean => !!state.report && !!state.report.attributes.parameters.metrics),
      tap(([metrics, state]: [Array<MetricComponentObject>, PublicReportsReportState]): void => {
        this.patchState({
          reportMetrics: state.report.attributes.parameters.metrics
            .map((slug: string): MetricComponentObject | undefined => metrics.find((metric: MetricComponentObject): boolean => metric.payload.attributes.slug === slug))
            .filter((metric: MetricComponentObject | undefined): boolean => !!metric)
        });
      })
    )
  );

  private readonly _conversionMetric$: Subscription = this.effect((): Observable<[Array<MetricComponentObject>, PublicReportsReportState]> => this._store.select(selectMetricComponentObjects)
    .pipe(
      withLatestFrom(this.state$),
      filter(([_, state]: [Array<MetricComponentObject>, PublicReportsReportState]): boolean => !!state.report && !!state.report.attributes.parameters.conversion_metric),
      tap(([metrics, state]: [Array<MetricComponentObject>, PublicReportsReportState]): void => {
        this.patchState({
          reportConversionMetric: metrics.find((metric: MetricComponentObject): boolean => metric.payload.attributes.slug === state.report.attributes.parameters.conversion_metric)
        });
      })
    )
  );

  private readonly _channel$: Subscription = this.effect((): Observable<[Serializers<Channel>, PublicReportsReportState]> => this._store.select(selectChannels)
    .pipe(
      withLatestFrom(this.state$),
      filter(([_, state]: [Serializers<Channel>, PublicReportsReportState]): boolean => !!state.report && !!state.report.attributes.parameters.channel),
      tap(([channels, state]: [Serializers<Channel>, PublicReportsReportState]): void => {
        this.patchState({
          reportChannel: channels.find((channel: Serializer<Channel>): boolean => channel.attributes.slug === state.report.attributes.parameters.channel)
        });
      })
    )
  );

  public onOpenPeriodSetting: () => void = this.effect((params$: Observable<void>): Observable<[void, PublicReportsReportState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([_, state]: [void, PublicReportsReportState]): void => {
      this._store.dispatch(reportOpenPeriodDialog({
        disableComparison: ['path-explorer', 'transaction-id'].includes(state.report.attributes.report_type)
      }));
    })
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _actions$: Actions,
    private readonly _router: Router,
    private readonly _service: PublicReportsService
  ) {
    super({
      publicReportData: null,
      report: null,
      reportType: null,
      reportName: null,
      reportDimensions: null,
      reportMetrics: null,
      reportConversionMetric: null,
      reportChannel: null,
      createBy: null,
      uuid: null,
      token: null,
      drawerIsOpened: true,
      loading: false,
      loaded: false
    });

    this._loadReport();
  }

  public openDrawer(): void {
    this.patchState({ drawerIsOpened: true });
  }

  public closeDrawer(): void {
    this.patchState({ drawerIsOpened: false });
  }

}
